import React, { useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../../store/hooks/useAuth";
import { useHasPermission } from "../../../store/hooks/useRbac";
import { PERMISSIONS } from "../../../utils/rbac/permissions";
import { RoleTypeEnum } from "../../../utils/constants";
import PageHeader from "../../common/PageHeader";
import Button from "../../common/Button";
import TabSelector from "../../common/TabSelector";
import ActionCard from "../../common/ActionCard";
import {
  Building2,
  Clock,
  CalendarDays,
  SlidersHorizontal,
  ToggleLeft,
  Mail,
  FileText,
  ShieldAlert,
  ArrowLeft,
  Plus,
} from "lucide-react";

import OrganizationOnboarding from "../../organization-Onboarding/OrganizationOnboarding";
import OrganizationConfig from "../../config-management/OrganizationConfig";
import HolidayConfig from "../../config-management/HolidayConfig";
import FilterManagement, {
  type FilterManagementHandle,
} from "../../filters/FilterManagement";
import FeatureFlagManagement from "../../admin-config/FeatureFlagManagement";
import EmailConfigPage from "../email-config/EmailConfigPage";
import EmailTemplatesPage from "../email-templates/EmailTemplatesPage";

export interface SettingsTab {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  iconClass?: string;
  permissions?: string[];
  superAdminOnly?: boolean;
  component: React.ReactNode;
  bare?: boolean;
}

const CommunicationTabContent: React.FC = () => {
  const [subTab, setSubTab] = React.useState<"config" | "templates">("config");
  const canViewEmailConfig = useHasPermission(PERMISSIONS.EMAIL_CONFIG_VIEW);
  const canViewEmailTemplates = useHasPermission(PERMISSIONS.EMAIL_TEMPLATES_VIEW);
  const { user } = useAuth();
  const isSuperAdmin = user?.role?.toLowerCase() === RoleTypeEnum.SUPER_ADMIN?.toLowerCase();

  const showConfig = isSuperAdmin || canViewEmailConfig;
  const showTemplates = isSuperAdmin || canViewEmailTemplates;

  React.useEffect(() => {
    if (!showConfig && showTemplates) {
      setSubTab("templates");
    } else if (showConfig && !showTemplates) {
      setSubTab("config");
    }
  }, [showConfig, showTemplates]);

  return (
    <div className="space-y-6">
      {showConfig && showTemplates && (
        <TabSelector
          tabs={[
            { k: "config" as const, label: "Email Configuration", icon: <Mail className="w-3.5 h-3.5" /> },
            { k: "templates" as const, label: "Email Templates", icon: <FileText className="w-3.5 h-3.5" /> },
          ]}
          active={subTab}
          onChange={setSubTab}
        />
      )}

      <div>
        {subTab === "config" && showConfig && <EmailConfigPage />}
        {subTab === "templates" && showTemplates && <EmailTemplatesPage />}
      </div>
    </div>
  );
};

export const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const isSuperAdmin =
    user?.role?.toLowerCase() === RoleTypeEnum.SUPER_ADMIN?.toLowerCase();

  // Dynamic RBAC Permission Checks (SuperAdmin maintains fallback)
  const canOrgOnboarding =
    useHasPermission([
      PERMISSIONS.ORGANIZATION_ONBOARDING_VIEW,
      PERMISSIONS.ORGANIZATION_ONBOARDING_MANAGE,
    ]) || isSuperAdmin;

  const canOrgConfig =
    useHasPermission([
      PERMISSIONS.ORGANIZATION_CONFIG_VIEW,
      PERMISSIONS.ORGANIZATION_CONFIG_MANAGE,
    ]) || isSuperAdmin;

  const canWeekOff =
    useHasPermission([
      PERMISSIONS.WEEK_OFF_CONFIG_VIEW,
      PERMISSIONS.WEEK_OFF_CONFIG_MANAGE,
    ]) || isSuperAdmin;

  const canMasterConfig =
    useHasPermission([
      PERMISSIONS.MASTER_CONFIG_VIEW,
      PERMISSIONS.MASTER_CONFIG_MANAGE,
    ]) || isSuperAdmin;

  const canMasterManage =
    useHasPermission([PERMISSIONS.MASTER_CONFIG_MANAGE]) || isSuperAdmin;

  const masterConfigRef = useRef<FilterManagementHandle | null>(null);

  const canFeatureFlags =
    useHasPermission([
      PERMISSIONS.FEATURE_FLAGS_VIEW,
      PERMISSIONS.FEATURE_FLAGS_MANAGE,
    ]) || isSuperAdmin;

  const canCommunication =
    useHasPermission([
      PERMISSIONS.EMAIL_CONFIG_VIEW,
      PERMISSIONS.EMAIL_CONFIG_MANAGE,
      PERMISSIONS.EMAIL_TEMPLATES_VIEW,
      PERMISSIONS.EMAIL_TEMPLATES_MANAGE,
    ]) || isSuperAdmin;

  const allTabs: SettingsTab[] = useMemo(
    () => [
      {
        id: "organization",
        label: "Organization Onboarding",
        icon: Building2,
        description: "Manage client organizations, domains, and global tenants",
        iconClass:
          "bg-[var(--color-warning-light)] text-[var(--color-warning-dark)]",
        permissions: [
          PERMISSIONS.ORGANIZATION_ONBOARDING_VIEW,
          PERMISSIONS.ORGANIZATION_ONBOARDING_MANAGE,
        ],
        superAdminOnly: true,
        component: <OrganizationOnboarding />,
      },
      {
        id: "working-hours",
        label: "Working Hours",
        icon: Clock,
        description: "Configure daily working thresholds, full-day and half-day rules",
        iconClass:
          "bg-[var(--color-info-light)] text-[var(--color-info)]",
        permissions: [
          PERMISSIONS.ORGANIZATION_CONFIG_VIEW,
          PERMISSIONS.ORGANIZATION_CONFIG_MANAGE,
        ],
        component: <OrganizationConfig />,
      },
      {
        id: "week-off",
        label: "Week Off & Holidays",
        icon: CalendarDays,
        description: "Set recurring weekly offs, alternate schedules, and custom holidays",
        iconClass:
          "bg-[var(--color-success-light)] text-[var(--color-success-dark)]",
        permissions: [
          PERMISSIONS.WEEK_OFF_CONFIG_VIEW,
          PERMISSIONS.WEEK_OFF_CONFIG_MANAGE,
        ],
        component: <HolidayConfig />,
      },
      {
        id: "master-config",
        label: "Master Config",
        icon: SlidersHorizontal,
        description: "Define dropdown options, departments, designations, and system lists",
        iconClass:
          "bg-[var(--color-primary-100)] text-[var(--color-primary-700)]",
        permissions: [
          PERMISSIONS.MASTER_CONFIG_VIEW,
          PERMISSIONS.MASTER_CONFIG_MANAGE,
        ],
        bare: true,
        component: <FilterManagement bare ref={masterConfigRef} />,
      },
      {
        id: "control-center",
        label: "Control Center",
        icon: ToggleLeft,
        description: "Toggle feature flags, beta modules, and global system capabilities",
        iconClass:
          "bg-[var(--color-error-light)] text-[var(--color-error-dark)]",
        permissions: [
          PERMISSIONS.FEATURE_FLAGS_VIEW,
          PERMISSIONS.FEATURE_FLAGS_MANAGE,
        ],
        component: <FeatureFlagManagement />,
      },
      {
        id: "communication",
        label: "Communication",
        icon: Mail,
        description: "Manage SMTP configurations, automated alerts, and email templates",
        iconClass:
          "bg-[var(--color-secondary-100)] text-[var(--color-secondary-600)]",
        permissions: [
          PERMISSIONS.EMAIL_CONFIG_VIEW,
          PERMISSIONS.EMAIL_CONFIG_MANAGE,
          PERMISSIONS.EMAIL_TEMPLATES_VIEW,
          PERMISSIONS.EMAIL_TEMPLATES_MANAGE,
        ],
        component: <CommunicationTabContent />,
      },
    ],
    [],
  );

  const availableTabs = useMemo(() => {
    return allTabs.filter((tab) => {
      if (tab.id === "organization") return canOrgOnboarding;
      if (tab.id === "working-hours") return canOrgConfig;
      if (tab.id === "week-off") return canWeekOff;
      if (tab.id === "master-config") return canMasterConfig;
      if (tab.id === "control-center") return canFeatureFlags;
      if (tab.id === "communication") return canCommunication;
      return false;
    });
  }, [
    allTabs,
    canOrgOnboarding,
    canOrgConfig,
    canWeekOff,
    canMasterConfig,
    canFeatureFlags,
    canCommunication,
  ]);

  const currentTabParam = searchParams.get("tab");
  const isDetailView = Boolean(
    currentTabParam && availableTabs.some((t) => t.id === currentTabParam)
  );

  const activeTab = useMemo(() => {
    if (isDetailView) {
      return availableTabs.find((t) => t.id === currentTabParam) || null;
    }
    return null;
  }, [isDetailView, currentTabParam, availableTabs]);

  const handleOpenSetting = (tabId: string) => {
    setSearchParams({ tab: tabId });
  };

  const handleBackToHub = () => {
    setSearchParams({});
  };

  if (availableTabs.length === 0) {
    return (
      <div className="w-full">
        <div className="max-w-4xl mx-auto text-center mt-12">
          <div className="w-16 h-16 bg-[var(--color-error-light)] text-[var(--color-error-dark)] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ShieldAlert className="w-8 h-8" />
          </div>
          <h2 className="text-heading-md font-bold text-[var(--color-text-primary)] mb-2">
            Access Restricted
          </h2>
          <p className="text-body-sm text-[var(--color-text-secondary)] max-w-md mx-auto">
            You do not have administrative permissions to view or modify system
            configurations. Please contact your organization administrator.
          </p>
        </div>
      </div>
    );
  }

  if (isDetailView && activeTab) {
    return (
      <div className="w-full space-y-6 animate-fade-in">
        <PageHeader
          title={activeTab.label}
          description={activeTab.description}
          breadcrumbs={[
            { label: "Home", path: "/dashboard" },
            { label: "Settings", path: "/admin/settings" },
            { label: activeTab.label },
          ]}
          actions={
            <>
              <Button
                appearance="secondary"
                size="small"
                onClick={handleBackToHub}
                icon={<ArrowLeft className="w-4 h-4" />}
              >
                All Settings
              </Button>
              {activeTab.id === "master-config" && canMasterManage && (
                <Button
                  appearance="primary"
                  size="small"
                  onClick={() => masterConfigRef.current?.openAdd()}
                  icon={<Plus className="w-4 h-4" />}
                >
                  Add Value
                </Button>
              )}
            </>
          }
        />

        {activeTab.bare ? (
          activeTab.component
        ) : (
          <div className="bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-card shadow-[var(--shadow-sm)] p-5 sm:p-8">
            {activeTab.component}
          </div>
        )}
      </div>
    );
  }

  const SETTINGS_GROUPS: { title: string; ids: string[] }[] = [
    { title: "Organization", ids: ["organization", "working-hours", "week-off"] },
    { title: "System & Data", ids: ["master-config", "control-center"] },
    { title: "Communication", ids: ["communication"] },
  ];

  return (
    <div className="w-full space-y-7 animate-fade-in">
      <PageHeader
        title="Configuration"
        description="Manage organization, features and communication"
        breadcrumbs={[
          { label: "Home", path: "/dashboard" },
          { label: "Settings" },
        ]}
      />

      <div className="flex flex-col gap-7">
        {SETTINGS_GROUPS.map((group) => {
          const groupTabs = availableTabs.filter((t) => group.ids.includes(t.id));
          if (groupTabs.length === 0) return null;
          return (
            <section key={group.title}>
              <h2 className="text-caption font-bold text-[var(--color-text-tertiary)] tracking-[0.06em] uppercase mx-0.5 mb-3">
                {group.title}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
                {groupTabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <ActionCard
                      key={tab.id}
                      layout="row"
                      icon={<Icon className="w-[22px] h-[22px]" />}
                      iconClassName={tab.iconClass}
                      title={tab.label}
                      description={tab.description}
                      onClick={() => handleOpenSetting(tab.id)}
                    />
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default SettingsPage;
